import React, { useEffect, FC, useState } from 'react';
import { Route, Switch, withRouter, Redirect, RouteComponentProps } from 'react-router-dom';
import { Dimmer, Loader } from 'semantic-ui-react';

import UserContext from './contexts/UserContext';
import RedirectContext from './contexts/RedirectContext';
import PrivateRoute from './components/components/privateRouteComponent';
import LogIn from './components/pages/login';
import ForgotPasswordPage from './components/pages/forgot-password';
import ChangeForgotPasswordPage from './components/pages/change-forgotten-password';
import ProfileSettings from './components/pages/profile-settings';
import News from './components/pages/news';
import FamilyAndFriends from './components/pages/family_friends';
import RelativeDetail from './components/components/relativeDetail';
import Settings from './components/pages/settings';
import Invites from './components/pages/invites';
import MessageRecipients from './components/pages/messageRecipients';
import MessageRecipientDetails from './components/pages/messageRecipientDetails';
import CreateMessageRecipient from './components/pages/createMessageRecipient/sso';
import Messages from './components/pages/messages';
import MessageReplyDetails from './components/pages/messages/messageDetails';
import Downloads from './components/pages/downloads';
import CommunityDownloads from './components/pages/communityDownloads';
import Activities from './components/pages/activities';
import ActivityDetail from './components/components/ActivityDetail';
import ArchivedActivities from './components/pages/archivedActivities';
import RequestDetail from './components/components/RequestDetail';
import Header from './components/components/header';
import Signup from './components/pages/signup';
import CommunityActivities from './components/pages/communityActivities';
import CommunityMenus from './components/pages/communityMenus';
import AutoLogin from './components/pages/auto-login';
import SSOAlexaUser from './components/pages/sso';
import SSORedirect from './components/pages/sso/redirect';
import Checkout from './components/pages/checkout';
import PublicCheckout from './components/pages/checkout/public';
import CheckoutSuccess from './components/pages/checkout/public/success';
import AddressBookDetails from './components/pages/address-book-details'
import AddressBookContactDetails from './components/pages/address-book-contacts-details'

import { Auth } from 'aws-amplify';
import { ToastContainer } from 'react-toastify';
import Reminders from './components/pages/reminders';
import * as Database from './services/db';
import AlexaLinkingLogin from './components/pages/alexa-linking';

import Payments from './components/pages/checkout/payment';

import AcceptAlexaInvites from './components/pages/accept-alexa-invites';

import './App.css';
import 'semantic-ui-less/semantic.less';
import 'react-toastify/dist/ReactToastify.css';
import { Steps, Hints } from 'intro.js-react';
import 'intro.js/introjs.css';
import LinkResidentList from './components/pages/link-resident-list';
import InviteAlexaUser from './components/pages/invite-alexa-user';
import AlexaInviteSSORedirect from './components/pages/accept-alexa-invites/redirect';
import AlexaUserInvitationAcceptPage from './components/pages/accept-alexa-invites/new-ui';
import FNFInviteeSignup from './components/pages/fnfInviteeSignup';

interface Props extends RouteComponentProps {}

interface userObjectInterface {
    cognitoUser: any;
    profile: any;
    registrants: any;
    username: any;
    authenticated: boolean;
}

const uninitiatedUserObject: userObjectInterface = {
    cognitoUser: {},
    profile: undefined,
    registrants: {
        list: [],
        activeRegistrantsIndexes: [],
    },
    username: undefined,
    authenticated: false,
};

const steps = [
    {
        element: '.feed-parent',
        intro: 'Welcome to your Feed. View all messages, requests, and activity of your Alexa Users here. Filter to view indvidual features and even download reports. If you accept a request, it will be added to your “My Requests” row.',
        position: 'top',
    },
    {
        element: '.Feedbutton',
        intro: 'Your feed. View messages, activities, and requests here',
        tooltipClass: 'feed-button-offset',
    },
    {
        element: '.Messagesbutton',
        intro: 'Text-style view of messages. See entire history conversations. ',
    },
    {
        element: '.Remindersbutton',
        intro: 'Set and manage reminders for your Alexa Users.',
    },
    {
        element: '.Send.Messagebutton',
        intro: 'Send individual or group messages directly',
    },
    {
        element: '.Familybutton',
        intro: 'Create your Circle of Trust. Add friends, family, and more via text or email. ',
    },
    {
        element: '.Alexa.Usersbutton',
        intro: 'Connect or add Alexa user(s). Sign in with their Amazon account',
    },
    {
        element: '.Settingsbutton',
        intro: 'Adjust your app settings including Notifications and message archives.',
        tooltipClass: 'feed-button-offset',
    },
    {
        element: '.resident-links',
        intro: 'The top row are your Alexa users. Select one, mutiple, or all to interact with',
        tooltipClass: 'resident-links-tooltip',
    },
];

const App: FC<Props> = (props) => {
    const [userObject, setUserObject] = useState(uninitiatedUserObject);
    const [loading, setLoading] = useState<boolean>(true);
    const [credentials, setCredentials] = useState({});
    const [stepsEnabled, setStepsEnabled] = useState(false);
    const [showSidebarProp, setShowSidebarProp] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);

    const setUser = (userObject) => {
        setUserObject(userObject);
    };

    const toggleRedirect = (redirect) => {
        props.history.push(redirect);
    };

    const isManager = () => {
        if (userObject && userObject.registrants) {
            return userObject.registrants.list.find((registrant) => registrant.manager);
        }
    };

    const isCommunityManager = () => {
        if (userObject && userObject.registrants) {
            return userObject.registrants.list.find((registrant) => registrant.isCommunityManager);
        }
    };

    const reloadRegistrantLists = async () => {
        const connectedRegistrants = await Database.getRelatedRegistrants();

        const activeRegistrantsIndexes =
            (userObject.registrants &&
                userObject.registrants.activeRegistrantsIndexes &&
                userObject.registrants.activeRegistrantsIndexes[0]) ||
            0;

        const registrants = {
            list: connectedRegistrants.residents,
            activeRegistrantsIndexes:
                connectedRegistrants.residents && connectedRegistrants.residents.length
                    ? [activeRegistrantsIndexes]
                    : [],
        };
        setUserObject({
            ...userObject,
            registrants,
        });
    };

    useEffect(() => {
        let cognitoUser;
        Auth.currentAuthenticatedUser({
            bypassCache: true,
        })
            .then((response) => {
                if (response.attributes && response.attributes['custom:accountType'] !== 'relative') {
                    return Auth.signOut();
                } else if (response !== 'not authenticated') {
                    cognitoUser = response;
                    return Database.getUserDbProfile();
                } else {
                    setLoading(false);
                    if (props.history.location.pathname !== '/login') {
                        toggleRedirect(props.history.location.pathname + props.history.location.search);
                    }
                    return;
                }
            })
            .then(async (profileResponse) => {
                const profile = profileResponse.userProfile;
                const connectedRegistrants = await Database.getRelatedRegistrants();
                const userObject = {
                    cognitoUser,
                    profile,
                    username: profileResponse.Username,
                    registrants: {
                        list: connectedRegistrants.residents,
                        activeRegistrantsIndexes:
                            connectedRegistrants.residents && connectedRegistrants.residents.length ? [0] : [],
                    },
                    authenticated: true,
                };
                setUserObject(userObject);
                setLoading(false);
                // if (props.history.location.pathname !== '/login') {
                //   	toggleRedirect(props.history.location.pathname + props.history.location.search);
                // }
                setDataLoaded(true);
            })
            .catch((error) => {
                setLoading(false);
                if (props.history.location.pathname !== '/login') {
                    toggleRedirect(props.history.location.pathname + props.history.location.search);
                }
            });
    }, []);

    useEffect(() => {
        //using data loaded is a precautionary step which might not be necessarily be needed.
        dataLoaded &&
            !localStorage.getItem('feedIntroShown') &&
            props.history.location.pathname === '/activities' &&
            setTimeout(() => {
                window.innerWidth > 600 && setShowSidebarProp(true);
                setStepsEnabled(true);
            }, 200);
    }, [props.history.location.pathname, dataLoaded]);

    useEffect(() => {
        console.log(props.location);
    }, [props.location.pathname]);

    return (
        <UserContext.Provider value={{ userObject, setUser, reloadRegistrantLists }}>
            <RedirectContext.Provider value={toggleRedirect}>
                {loading ? (
                    <Dimmer active={loading} inverted>
                        <Loader active={loading} />
                    </Dimmer>
                ) : (
                    <>
                        {userObject.authenticated &&
                        !(
                            ['/signup', '/checkout', '/auto-login', '/public/checkout-success'].includes(
                                props.location.pathname,
                            ) ||
                            props.location.pathname.includes('/public/checkout') ||
                            props.location.pathname.includes('/accept-alexa-invites/')
                        ) ? (
                            <Header {...props} showSidebarProp={showSidebarProp} />
                        ) : null}
                        <Switch>
                            <PrivateRoute path="/news" component={News} />
                            <PrivateRoute
                                path="/settings"
                                component={isManager() ? Settings : () => <Redirect to={{ pathname: '/activities' }} />}
                            />
                            <PrivateRoute path="/family_friends/:relativeId" component={RelativeDetail} />
                            <PrivateRoute path="/family_friends" component={FamilyAndFriends} />
                            <PrivateRoute path="/profile-settings" component={ProfileSettings} />
                            <PrivateRoute path="/message-recipients/:messageRecipientId/address-book/contacts/:contactId" component={AddressBookContactDetails}/>

                            <PrivateRoute path="/message-recipients/:messageRecipientId/address-book" component={AddressBookDetails}/>
                            <PrivateRoute path="/message-recipients/create" component={CreateMessageRecipient} />
                            <PrivateRoute
                                path="/message-recipients/:messageRecipientId"
                                component={MessageRecipientDetails}
                            />
                            <PrivateRoute path="/message-recipients" component={MessageRecipients} />
                            <PrivateRoute path="/messages/:messageRecipientId" component={MessageReplyDetails} />
                            <PrivateRoute path="/messages" component={Messages} />
                            <PrivateRoute path="/requests/:requestId" component={RequestDetail} />
                            <PrivateRoute path="/activities/:activityId" component={ActivityDetail} />
                            <PrivateRoute path="/archives" component={ArchivedActivities} />
                            <PrivateRoute path="/activities" component={Activities} />
                            <PrivateRoute
                                path="/community-activities"
                                component={
                                    isCommunityManager()
                                        ? CommunityActivities
                                        : () => <Redirect to={{ pathname: '/activities' }} />
                                }
                            />
                            <PrivateRoute
                                path="/community-menus"
                                component={
                                    isCommunityManager()
                                        ? CommunityMenus
                                        : () => <Redirect to={{ pathname: '/activities' }} />
                                }
                            />
                            <PrivateRoute path="/reminders" component={Reminders} />

                            <Route path="/alexa-linking" component={AlexaLinkingLogin} />
                            <PrivateRoute path="/link-resident-list" component={LinkResidentList} />

                            <Route
                                path="/login/:token?/:registrantId?/:receivedContact?"
                                render={
                                    userObject.authenticated
                                        ? () => <Redirect to={{ pathname: '/activities' }} />
                                        : (props) => <LogIn {...props} />
                                }
                            />
                            <Route
                                path="/signup"
                                render={(props) => (
                                    <Signup {...props} credentials={credentials} setCredentials={setCredentials} />
                                )}
                            />
                            <Route
                                path="/forgot-password"
                                render={
                                    userObject.authenticated
                                        ? () => <Redirect to={{ pathname: '/activities' }} />
                                        : (props) => <ForgotPasswordPage {...props} />
                                }
                            />
                            <Route
                                path="/change-forgotten-password"
                                render={
                                    userObject.authenticated
                                        ? () => <Redirect to={{ pathname: '/activities' }} />
                                        : (props) => <ChangeForgotPasswordPage {...props} />
                                }
                            />
                            <Route path="/downloads/community" component={CommunityDownloads} />
                            <Route path="/downloads" component={Downloads} />
                            <Route path="/auto-login" component={AutoLogin} />
                            <PrivateRoute path="/sso" component={SSOAlexaUser} />
                            
                            <Route path="/sso-redirect" component={SSORedirect} />
                            <PrivateRoute path="/invites" component={Invites} />
                            <PrivateRoute path="/checkout" component={Checkout} />
                            <Route path="/public/checkout/:paymentManagerId" component={PublicCheckout}></Route>
                            <PrivateRoute path="/payments" component={Payments} />
                            <Route path="/public/checkout-success" component={CheckoutSuccess}></Route>
                            <PrivateRoute path="/invite-alexa-user" component={InviteAlexaUser} />
                            <Route path="/accept-alexa-invites/:token" component={AcceptAlexaInvites} />
                            {/* <Route path="/alexa-invite-accept-page" component={AlexaUserInvitationAcceptPage} /> */}
                            <Route path="/signup-fnf-invite" component={FNFInviteeSignup} />
                            <Route path="/alexa-invite-sso-redirect" component={AlexaInviteSSORedirect} />
                            <Redirect from="/" to="/activities" />
                            <Redirect from="/*" to="/activities" />
                            <Route render={() => <h1>404 Not Found</h1>} />
                        </Switch>
                        <Steps
                            enabled={stepsEnabled}
                            steps={steps}
                            initialStep={0}
                            onExit={() => {
                                setShowSidebarProp(false);
                                localStorage.setItem('feedIntroShown', 'yes');
                            }}
                            onBeforeChange={(nextStepIndex) => {
                                if (nextStepIndex == 1) {
                                    setShowSidebarProp(true);
                                }
                                if (window.innerWidth <= 600) {
                                    if (nextStepIndex == 8) {
                                        setShowSidebarProp(false);
                                    }
                                    if (nextStepIndex == 7) {
                                        setShowSidebarProp(true);
                                    }
                                }
                            }}
                        />
                    </>
                )}

                <ToastContainer />
            </RedirectContext.Provider>
        </UserContext.Provider>
    );
};

export default withRouter(App);
